import type { File } from '../../domain/entities/file.ts';
import type { RecordRef } from '../../domain/entities/notification.ts';
import type { FunctionCatalog, FunctionsPort } from '../../ports/functions-port.ts';

/**
 * The attachments on a record, for somebody who may not list files.
 *
 * `subscribeToAttachments` in `attachments.ts` is the internal path: two
 * constrained queries over `files`, live. This is the external one, and it
 * exists because listing `files` is internal-only and must stay that way —
 * the document carries `storagePath`, and `jobId` is a field rather than a
 * path segment, so a `list` rule cannot ask whether the caller is on the
 * job without one read per returned file.
 *
 * The decision and the three designs rejected are written up in
 * `docs/features/11-files-messaging.md` §7. The short version: the ids come
 * from a callable that checks the record first, because you may enumerate
 * the attachments of a record you can read and of no other.
 */

/**
 * What a caller outside the office is told about an attached file.
 *
 * Deliberately not a `File`. **`storagePath` is absent**, and that absence
 * is the point: the only reason listing files is internal-only is that the
 * document carries the path to the bytes. `visibility` is absent too — an
 * owner has no use for it, and "shared with subs" is internal workflow.
 */
export interface AttachmentSummary {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly contentType: string;
  readonly createdAt: string;
}

/**
 * Project a file document to what may leave the company.
 *
 * In core rather than in the function so the server that builds the
 * summary and the client that renders it cannot disagree about the fields,
 * and so a field added to `File` later is not added to this by accident.
 */
export function attachmentSummaryOf(
  file: Pick<File, 'id' | 'name' | 'size' | 'contentType' | 'createdAt'>,
): AttachmentSummary {
  return {
    id: file.id,
    name: file.name,
    size: file.size,
    contentType: file.contentType,
    createdAt: file.createdAt,
  };
}

/**
 * The same, from a document nobody has validated yet.
 *
 * The server reads raw Firestore data, and a full `FileSchema.parse` there
 * would be the wrong strictness: one document written by an older version,
 * missing one field, would throw and blank the **whole** strip rather than
 * omit one photograph. So a document that cannot produce a summary is
 * dropped and returns null, and the caller logs it.
 */
export function attachmentSummaryFrom(
  id: string,
  data: Readonly<Record<string, unknown>>,
): AttachmentSummary | null {
  const name = typeof data.name === 'string' ? data.name : '';
  const contentType = typeof data.contentType === 'string' ? data.contentType : '';
  const size = typeof data.size === 'number' && Number.isFinite(data.size) ? data.size : null;
  const createdAt = typeof data.createdAt === 'string' ? data.createdAt : '';
  if (!id || !name || !contentType || size === null || !createdAt) return null;
  return attachmentSummaryOf({ id, name, size, contentType, createdAt });
}

/** The record kinds that can carry attachments and live in a readable collection. */
export const ATTACHABLE_KINDS = [
  'dailyLog',
  'todo',
  'scheduleItem',
  'job',
  /**
   * Added with the strips on those two records (P8-12a).
   *
   * **This list is narrower than `RecordRefSchema` on purpose.** A kind
   * belongs here only once the server knows the collection its documents
   * live in and can therefore read one to check the caller may see it —
   * see `recordPathFor`. A kind added here without that is a record
   * nobody can check, which is worse than one nobody can attach to.
   */
  'warrantyClaim',
  'specSection',
] as const;
export type AttachableKind = (typeof ATTACHABLE_KINDS)[number];

/**
 * The port's union and this list must be the same set.
 *
 * A port may not import a use case, so the union is written out there
 * and checked here — in both directions, because one direction only
 * catches the list shrinking. Drift becomes a compile error rather than
 * a call the server rejects as an invalid argument at runtime.
 */
type PortKind = FunctionCatalog['recordAttachments']['input']['recordKind'];
const _attachableMatchesPort: PortKind = null as unknown as AttachableKind;
const _portMatchesAttachable: AttachableKind = null as unknown as PortKind;
void _attachableMatchesPort;
void _portMatchesAttachable;

export function isAttachableKind(kind: string): kind is AttachableKind {
  return (ATTACHABLE_KINDS as readonly string[]).includes(kind);
}

export interface RecordAttachmentsContext {
  readonly functions: FunctionsPort;
  readonly companyId: string;
}

/**
 * Ask the server what is attached to this record.
 *
 * Not live, and `docs/features/11-files-messaging.md` §7 says why: every
 * other panel in the portal is a subscription, but a photograph strip on
 * last Tuesday's log has nothing to stream. Giving up realtime here costs
 * nothing a person would notice and saves a mirror collection.
 */
export async function recordAttachments(
  context: RecordAttachmentsContext,
  record: RecordRef,
): Promise<readonly AttachmentSummary[]> {
  if (!isAttachableKind(record.kind)) return [];
  const result = await context.functions.call('recordAttachments', {
    companyId: context.companyId,
    recordKind: record.kind,
    recordId: record.id,
    ...(record.jobId ? { jobId: record.jobId } : {}),
  });
  return result.attachments;
}

/**
 * A link to one attachment's bytes, for a caller holding only a summary.
 *
 * `downloadUrlFor` in `file-actions.ts` takes a whole `File` and checks the
 * status and the stored path before asking. Neither is available out here,
 * and that is deliberate — a summary carries no `storagePath`, and
 * `recordAttachments` has already dropped anything not `ready`. So this is
 * the same call with the client-side pre-checks left out rather than faked:
 * the function re-checks the level, the job, the audience and the status
 * anyway, which is the only place those answers are authoritative.
 */
export async function attachmentDownloadUrl(
  context: RecordAttachmentsContext,
  attachmentId: string,
): Promise<string> {
  const result = await context.functions.call('fileDownloadUrl', {
    companyId: context.companyId,
    fileId: attachmentId,
  });
  return result.url;
}
